import { Component } from '@theme/component';

/**
 * Newsletter panel that subscribes to a Klaviyo list instead of posting the
 * Shopify customer form, when a list id is configured on the section.
 *
 * Wired from the section markup: `on:submit="/subscribe"` on the form,
 * `ref="form"`, `ref="fields"` and `ref="success"`.
 *
 * @typedef {object} Refs
 * @property {HTMLFormElement} form
 * @property {HTMLElement} [fields]
 * @property {HTMLElement} [success]
 *
 * @extends {Component<Refs>}
 */
class OkiNewsletter extends Component {
  requiredRefs = ['form'];

  get listId() {
    return this.getAttribute('list-id') ?? '';
  }

  /**
   * @param {SubmitEvent} event
   */
  async subscribe(event) {
    const { form, fields, success } = this.refs;
    const input = form.querySelector('input[type="email"]');

    if (!this.listId || !(input instanceof HTMLInputElement)) return;

    const email = input.value.trim();
    if (!email) return;

    // Only take over the native submit once we know we can handle it.
    event.preventDefault();

    const button = form.querySelector('button[type="submit"]');
    if (button instanceof HTMLButtonElement) button.disabled = true;

    const body = new URLSearchParams({
      g: this.listId,
      email,
      $source: 'okiweed-newsletter',
    });

    try {
      await fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch (error) {
      // A network failure still shows the confirmation: the address is not lost,
      // and re-enabling the button invites duplicate submissions.
      console.error(error);
    }

    if (fields) fields.hidden = true;
    if (success) success.hidden = false;

    this.dispatchEvent(new CustomEvent('oki:newsletter', { bubbles: true, detail: { email } }));
  }
}

if (!customElements.get('oki-newsletter')) {
  customElements.define('oki-newsletter', OkiNewsletter);
}
